import React, { useState, useEffect, useCallback, useRef } from "react";

const PAGE_SIZE = 8;

const ImageGallery = ({
	images = [],
	title = "Moments From The Launch",
	subtitle = "",
}) => {
	const [page, setPage] = useState(0);
	const [lightboxIndex, setLightboxIndex] = useState(null);
	const sectionRef = useRef(null);

	const totalPages = Math.ceil(images.length / PAGE_SIZE);
	const pageImages = images.slice(
		page * PAGE_SIZE,
		page * PAGE_SIZE + PAGE_SIZE,
	);

	const goToPage = (p) => {
		setPage(p);
		sectionRef.current?.scrollIntoView({
			behavior: "smooth",
			block: "start",
		});
	};

	const closeLightbox = useCallback(() => setLightboxIndex(null), []);
	const goNext = useCallback(
		() => setLightboxIndex((i) => (i + 1) % images.length),
		[images.length],
	);
	const goPrev = useCallback(
		() => setLightboxIndex((i) => (i - 1 + images.length) % images.length),
		[images.length],
	);

	useEffect(() => {
		if (lightboxIndex === null) return;
		const handleKey = (e) => {
			if (e.key === "Escape") closeLightbox();
			if (e.key === "ArrowRight") goNext();
			if (e.key === "ArrowLeft") goPrev();
		};
		document.addEventListener("keydown", handleKey);
		return () => document.removeEventListener("keydown", handleKey);
	}, [lightboxIndex, closeLightbox, goNext, goPrev]);

	useEffect(() => {
		document.body.style.overflow = lightboxIndex !== null ? "hidden" : "";
		return () => {
			document.body.style.overflow = "";
		};
	}, [lightboxIndex]);

	const isEmpty = !images.length;

	const PrevIcon = () => (
		<svg
			className="h-5 w-5"
			fill="none"
			stroke="currentColor"
			viewBox="0 0 24 24"
			aria-hidden="true"
		>
			<path
				strokeLinecap="round"
				strokeLinejoin="round"
				strokeWidth={2}
				d="M15 19l-7-7 7-7"
			/>
		</svg>
	);

	const NextIcon = () => (
		<svg
			className="h-5 w-5"
			fill="none"
			stroke="currentColor"
			viewBox="0 0 24 24"
			aria-hidden="true"
		>
			<path
				strokeLinecap="round"
				strokeLinejoin="round"
				strokeWidth={2}
				d="M9 5l7 7-7 7"
			/>
		</svg>
	);

	return (
		<section
			ref={sectionRef}
			className="w-full bg-white scroll-mt-28"
			aria-label="Event photo gallery"
		>
			<div className="flex w-full justify-center px-4 py-12 sm:px-6 sm:py-16 lg:px-8 lg:py-24">
				<div className="w-full max-w-[1204px]">
					{/* Header */}
					<div className="flex flex-wrap items-end justify-between gap-4">
						<div className="max-w-[900px]">
							<p className="m-0 text-[16px] font-semibold uppercase text-[#116DFD]">
								Event Gallery
							</p>
							<h2 className="mb-0 mt-4 text-[39px] font-bold leading-[1.08] tracking-[-0.03em] text-[#161F2F]">
								{title}
							</h2>
							{subtitle && (
								<p className="mb-0 mt-5 max-w-[700px] text-[16px] leading-[1.55] text-[#333333]">
									{subtitle}
								</p>
							)}
						</div>
						{!isEmpty && (
							<p className="m-0 shrink-0 text-[14px] text-[#6A7280]">
								{images.length} photos &middot; page {page + 1}{" "}
								of {totalPages}
							</p>
						)}
					</div>

					{/* Grid or empty state */}
					{isEmpty ? (
						<div className="mt-10 flex flex-col items-center justify-center rounded-[20px] border border-dashed border-[#C8D7F5] bg-[#F4F8FF] py-20 px-6 text-center">
							<div className="flex h-16 w-16 items-center justify-center rounded-full bg-[#EAF0FD]">
								<svg
									className="h-8 w-8 text-[#116DFD]"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
									aria-hidden="true"
								>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth={1.5}
										d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
									/>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth={1.5}
										d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"
									/>
								</svg>
							</div>
							<h3 className="mb-0 mt-5 text-[20px] font-bold tracking-[-0.02em] text-[#161F2F]">
								Photos coming soon
							</h3>
							<p className="mb-0 mt-3 max-w-[420px] text-[15px] leading-[1.6] text-[#6A7280]">
								Event photos from April 22nd will be added here
								shortly. Check back soon.
							</p>
						</div>
					) : (
						<>
							<div className="mt-10 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
								{pageImages.map((img, i) => {
									const globalIndex = page * PAGE_SIZE + i;
									return (
										<button
											key={globalIndex}
											type="button"
											onClick={() =>
												setLightboxIndex(globalIndex)
											}
											aria-label={`View photo ${globalIndex + 1}${img.caption ? `: ${img.caption}` : ""}`}
											className="group relative aspect-square overflow-hidden rounded-[14px] bg-[#F1F4F9] border border-transparent hover:border-[#116DFD80] transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#116DFD] cursor-pointer"
										>
											<img
												src={img.src}
												alt={
													img.caption ||
													`Event photo ${globalIndex + 1}`
												}
												className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
											/>
											<div className="absolute inset-0 flex items-center justify-center bg-[#09152E]/0 transition-all duration-200 group-hover:bg-[#09152E]/35">
												<svg
													className="h-8 w-8 text-white opacity-0 transition-opacity duration-200 group-hover:opacity-100"
													fill="none"
													stroke="currentColor"
													viewBox="0 0 24 24"
													aria-hidden="true"
												>
													<path
														strokeLinecap="round"
														strokeLinejoin="round"
														strokeWidth={2}
														d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zm-4-7v3m0 0v3m0-3h3m-3 0H7"
													/>
												</svg>
											</div>
										</button>
									);
								})}
							</div>

							{/* Pagination */}
							{totalPages > 1 && (
								<div className="mt-10 flex items-center justify-center gap-2">
									<button
										type="button"
										onClick={() => goToPage(page - 1)}
										disabled={page === 0}
										aria-label="Previous page"
										className="flex h-10 w-10 items-center justify-center rounded-full border border-[#D6E3FF] bg-white text-[#116DFD] transition-all hover:border-[#116DFD] hover:bg-[#EAF0FD] disabled:cursor-not-allowed disabled:opacity-30"
									>
										<PrevIcon />
									</button>

									<div className="flex items-center gap-1">
										{Array.from(
											{ length: totalPages },
											(_, i) => {
												const isActive = i === page;
												const isNearActive =
													Math.abs(i - page) <= 1 ||
													i === 0 ||
													i === totalPages - 1;

												if (
													!isNearActive &&
													Math.abs(i - page) === 2
												) {
													return (
														<span
															key={i}
															className="px-1 text-[#9CA3AF]"
														>
															&hellip;
														</span>
													);
												}
												if (!isNearActive) return null;

												return (
													<button
														key={i}
														type="button"
														onClick={() =>
															goToPage(i)
														}
														aria-label={`Go to page ${i + 1}`}
														aria-current={
															isActive
																? "page"
																: undefined
														}
														className={`flex h-10 min-w-[40px] items-center justify-center rounded-full px-3 text-[14px] font-semibold transition-all ${
															isActive
																? "bg-[#116DFD] text-white shadow-[0_4px_12px_rgba(17,109,253,0.3)]"
																: "border border-[#D6E3FF] bg-white text-[#374151] hover:border-[#116DFD] hover:bg-[#EAF0FD] hover:text-[#116DFD]"
														}`}
													>
														{i + 1}
													</button>
												);
											},
										)}
									</div>

									<button
										type="button"
										onClick={() => goToPage(page + 1)}
										disabled={page === totalPages - 1}
										aria-label="Next page"
										className="flex h-10 w-10 items-center justify-center rounded-full border border-[#D6E3FF] bg-white text-[#116DFD] transition-all hover:border-[#116DFD] hover:bg-[#EAF0FD] disabled:cursor-not-allowed disabled:opacity-30"
									>
										<NextIcon />
									</button>
								</div>
							)}
						</>
					)}
				</div>
			</div>

			{/* Lightbox */}
			{lightboxIndex !== null && (
				<div
					role="dialog"
					aria-modal="true"
					aria-label="Image viewer"
					className="fixed inset-0 z-50 flex items-center justify-center bg-black/90"
					onClick={closeLightbox}
				>
					<button
						type="button"
						onClick={closeLightbox}
						aria-label="Close gallery"
						className="absolute right-4 top-4 rounded-full p-2 text-white/70 transition-colors hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
					>
						<svg
							className="h-7 w-7"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
							aria-hidden="true"
						>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth={2}
								d="M6 18L18 6M6 6l12 12"
							/>
						</svg>
					</button>

					<button
						type="button"
						onClick={(e) => {
							e.stopPropagation();
							goPrev();
						}}
						aria-label="Previous image"
						className="absolute left-3 sm:left-6 rounded-full border border-white/20 bg-white/10 p-2 text-white/70 backdrop-blur-sm transition-colors hover:bg-white/20 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
					>
						<svg
							className="h-6 w-6"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
							aria-hidden="true"
						>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth={2}
								d="M15 19l-7-7 7-7"
							/>
						</svg>
					</button>

					<div
						className="flex max-h-[90vh] max-w-[88vw] flex-col items-center gap-3"
						onClick={(e) => e.stopPropagation()}
					>
						<img
							src={images[lightboxIndex].src}
							alt={
								images[lightboxIndex].caption ||
								`Event photo ${lightboxIndex + 1}`
							}
							className="max-h-[80vh] max-w-full rounded-[14px] object-contain shadow-2xl"
						/>
						<div className="flex items-center gap-4 text-sm text-white/60">
							{images[lightboxIndex].caption && (
								<span>{images[lightboxIndex].caption}</span>
							)}
							<span>
								{lightboxIndex + 1} / {images.length}
							</span>
						</div>
					</div>

					<button
						type="button"
						onClick={(e) => {
							e.stopPropagation();
							goNext();
						}}
						aria-label="Next image"
						className="absolute right-3 sm:right-6 rounded-full border border-white/20 bg-white/10 p-2 text-white/70 backdrop-blur-sm transition-colors hover:bg-white/20 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
					>
						<svg
							className="h-6 w-6"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
							aria-hidden="true"
						>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth={2}
								d="M9 5l7 7-7 7"
							/>
						</svg>
					</button>
				</div>
			)}
		</section>
	);
};

export default ImageGallery;
